import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import FormHelperText from '@material-ui/core/FormHelperText';

import Icon from '@material-ui/core/Icon';

import ReportFilters from './ReportFilters';
import ReportToolbar from './ReportToolbar';
import ToggleShowEmail from './ToggleShowEmail';

const parseNum = num => parseFloat(num || 0);

const formatNumber = number => {
    return parseNum(number).toFixed(2);
}

const useStyles = makeStyles(theme => ({
    paper: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(3),
        '& .report-filters--search .report-filters--control': {
            width: '300px !important',
        },
    },
    table: {
        minWidth: 650,
        '& .iconWrapper': {
            opacity: 0.9,
            verticalAlign: 'middle',
            cursor: 'pointer',
            paddingLeft: 2,
            paddingRight: 2,
            display: 'inline-block',
        }
    },
    weddingDate: {
        minWidth: 165,
    },
    weddingTime: {
        minWidth: 108
    },
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        '& .MuiTableCell-head': {
            color: theme.palette.primary.contrastText,
        },
    },
    columnArrow: {
        '& .iconWrapper': {
            visibility: 'hidden',
        },
        '&:hover .iconWrapper': {
            opacity: 0.4,
            visibility: 'visible',
        },
    },
}));

const WeddingLocationsReport = () => {
    const classes = useStyles();
    const reportUrl = '/api/reports/booked-complete';
    const reportTitle = 'Booked and Complete Weddings Report';
    const pageTitle = `${reportTitle} - Enduring Promises`;
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [ordering, setOrdering] = React.useState('wedding_date');

    const now = new Date();
    const past = new Date(now.getFullYear(), 0, 1);
    const current = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0, 0);

    const formatDate = val => {
        const month = val.getMonth() + 1;
        const day = val.getDate();
        return `${val.getFullYear()}-${month < 10 ? '0'+month : month}-${day < 10 ? '0'+day : day}`;
    }

    const [state, setState] = React.useState({
        start_date: formatDate(past),
        end_date: formatDate(current),
        search: '',
        search_field: 'venue',
    });

    const searchFields = [
        { value: 'venue', label: 'Venue' },
        { value: 'location', label: 'Location' },
        { value: 'officiant', label: 'Officiant' },
        { value: 'referral', label: 'Referral' },
    ];

    const getData = state => {
        new Promise((resolve, reject) => {
            fetch(`${reportUrl}?search=${state.search}&searchField=${state.search_field}&start=${state.start_date}&end=${state.end_date}&orderBy=${ordering}`)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setData(result);
                    resolve();
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    useEffect(() => {
        document.title = pageTitle;
        getData(state);
    }, [ordering]);

    const handleSubmit = () => {
        getData(state);
    };

    const [showEmails, setShowEmails] = React.useState(true);

    const csvData = (data) => {
        const rows = [];
        Object.values(data).forEach(row => {
            rows.push([
                `${row.wedding_date_formatted} ${row.wedding_time_formatted}`,
                row.inquiry_date,
                row.date_booked_formatted,
                row.bride_full_name,
                row.groom_full_name,
                row.bride_email,
                row.groom_email,
                row.officiant ? row.officiant.name : '',
                row.venue,
                row.location,
                row.couple_referred,
                row.referral_vendor_venue,
                row.fees ? formatNumber(row.fees.ceremony_fee) : "0.00",
                row.has_rehearsal ? "Yes" : "No",
                row.fees ? formatNumber(row.fees.rehearsal_fee) : "0.00",
            ]);
        })
        return {
            filename: 'booked-and-complete-weddings-report.csv',
            columns: ['Wedding Date', 'Inquiry Date', 'Booked Date', 'Bride Name', 'Groom Name', 'Bride Email', 'Groom Email', 'Officiant', 'Venue', 'Location', 'Referral', 'Referral Details', 'Ceremony Fee', "Has Researsal", "Rehearsal Fee"],
            rows,
        }
    }

    return (
        <Paper square className={classes.paper}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6">{reportTitle}</Typography>
                </Grid>
                <Grid item xs={8}>
                    <Box>
                        <ReportFilters state={state} setState={setState} handleSubmit={handleSubmit} searchFields={searchFields} />
                        <FormHelperText>Note: Can use comma-separated list in search box</FormHelperText>
                    </Box>
                    <ToggleShowEmail showEmails={showEmails} setShowEmails={setShowEmails} />
                </Grid>
                <Grid item xs={4}>
                    <Grid
                        container
                        direction="row"
                        justify="flex-end"
                        alignItems="center"
                    >
                        {data !== null && <ReportToolbar data={csvData(data)} />}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined" square>
                        <Table className={classes.table} size="small">
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    <TableCell
                                        className={ordering == 'wedding_date' ? [classes.columnOrdered, classes.weddingDate].join(' ') : [classes.columnArrow, classes.weddingDate].join(' ')}
                                        align="left"
                                    >
                                        Wedding Date
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('wedding_date')}>arrow_downward</Icon>
                                        </span>
                                    </TableCell>
                                    <TableCell align="left">Inquiry Date</TableCell>
                                    <TableCell align="left">Booked Date</TableCell>
                                    <TableCell align="left">Status</TableCell>
                                    <TableCell align="left">Bride Name</TableCell>
                                    <TableCell align="left">Groom Name</TableCell>
                                    {showEmails && (
                                        <React.Fragment>
                                            <TableCell align="left">Bride Email</TableCell>
                                            <TableCell align="left">Groom Email</TableCell>
                                        </React.Fragment>
                                    )}
                                    <TableCell align="left" className={ordering == 'officiant' ? classes.columnOrdered : classes.columnArrow}>
                                        Officiant
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('officiant')}>arrow_downward</Icon>
                                        </span>
                                    </TableCell>
                                    <TableCell align="left" className={ordering == 'venue' ? classes.columnOrdered : classes.columnArrow}>
                                        Venue
                                        <span className='iconWrapper'>
                                            <Icon fontSize='small' onClick={() => setOrdering('venue')}>arrow_downward</Icon>
                                        </span>
                                    </TableCell>
                                    <TableCell align="left">Location</TableCell>
                                    <TableCell align="left">Referral</TableCell>
                                    <TableCell align="left">Referral Details</TableCell>
                                    <TableCell align="left">Ceremony Fee</TableCell>
                                    <TableCell align="left">Rehearsal</TableCell>
                                    <TableCell align="left">Researsal Fee</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {!loading && data !== null && data.map((row, i) => {
                                return (
                                    <TableRow key={`row-${i}`}>
                                        <TableCell align="left">
                                            <a href={`/admin/couples/${row.id}`} target="_blank">{row.wedding_date_formatted} {row.wedding_time_formatted}</a>
                                        </TableCell>
                                        <TableCell align="left">{row.inquiry_date}</TableCell>
                                        <TableCell align="left">{row.date_booked_formatted}</TableCell>
                                        <TableCell align="left">{row.couple_type}</TableCell>
                                        <TableCell align="left">{row.bride_full_name}</TableCell>
                                        <TableCell align="left">{row.groom_full_name}</TableCell>
                                        {showEmails && (
                                            <React.Fragment>
                                                <TableCell align="left">{row.bride_email}</TableCell>
                                                <TableCell align="left">{row.groom_email}</TableCell>
                                            </React.Fragment>
                                        )}
                                        <TableCell align="left">{row.officiant ? row.officiant.name : ''}</TableCell>
                                        <TableCell align="left">{row.venue}</TableCell>
                                        <TableCell align="left">{row.location}</TableCell>
                                        <TableCell align="left">{row.couple_referred}</TableCell>
                                        <TableCell align="left">{row.referral_vendor_venue}</TableCell>
                                        <TableCell align="left">{"$"}{row.fees ? formatNumber(row.fees.ceremony_fee) : "0.00"}</TableCell>
                                        <TableCell align="left">{row.has_rehearsal ? "Yes" : "No"}</TableCell>
                                        <TableCell align="left">{"$"}{row.fees ? formatNumber(row.fees.rehearsal_fee) : "0.00"}</TableCell>
                                    </TableRow>
                                )
                            })}
                            {!loading && data !== null && data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={showEmails ? 14 : 12} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={showEmails ? 14 : 12} align="center">
                                        <CircularProgress color="primary" />
                                    </TableCell>
                                </TableRow>
                            )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default WeddingLocationsReport;
